import moment from 'moment';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Spinner } from '../../../../../components/Spinner';
import VirtualizedList from '../../../../../components/VirtualizedList';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import {
  REWARDS_STATUS_BADGE,
  REWARDS_SURVEY_STATUS_BADGE,
} from '../../../../../constants/consumers';
import { getTextWidthInOneLine } from '../../../../../utils/strings';

function ConsumerRewardsTab({ consumerId, refreshTab, setRefreshTab }) {
  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  const getRewards = useCallback(async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/get_rewards`,
      method: 'GET',
      params: { consumerId },
    };

    try {
      const response = await makeRequest(config);

      setRewards(response);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  }, [apiOriginConsumer, consumerId, getAccessTokenSilently]);

  useEffect(() => {
    getRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerId]);

  useEffect(() => {
    if (refreshTab === 'rewards') {
      getRewards();
      setRefreshTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTab]);

  const rewardRequest = useCallback(
    async (type, rewardId) => {
      if (window.confirm('Are you sure?')) {
        setSpinner(true);
        const token = await getAccessTokenSilently();

        if (!token) {
          return;
        }

        let url = '';

        let data = {};

        if (type === 'delete') {
          url = `${apiOriginConsumer}/rewards/test/delete`;
          data = JSON.stringify({ rewardId });
        }

        if (type === 'deleteAll') {
          url = `${apiOriginConsumer}/rewards/test/delete_all_consumer`;
          data = JSON.stringify({ consumerId });
        }

        if (type === 'processAll') {
          url = `${apiOriginConsumer}/rewards/process`;
          data = JSON.stringify({ consumerId });
        }

        try {
          await makeRequest({ token, method: 'POST', url, data });
          await getRewards();
        } catch (error) {
          toast.error(error);
        } finally {
          setSpinner(false);
        }
      }
    },
    [getRewards, getAccessTokenSilently, apiOriginConsumer, consumerId],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: 'category',
        header: 'Category',
        width: 120,
      },
      {
        key: 'name',
        header: 'Name',
        width: 320,
      },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        renderValue: (value, row) => {
          const badge =
            row.category === 'SURVEY' ? REWARDS_SURVEY_STATUS_BADGE : REWARDS_STATUS_BADGE;

          return (
            <Badge className="bo-inline-badge bo-w-100" bg={badge[value]?.color}>
              {badge[value]?.text}
            </Badge>
          );
        },
      },
      {
        key: 'bonus',
        header: 'BridgePoints™ Grant',
        width: 220,
        renderValue: (value, row) => {
          if (
            row.status === 'OFFERED' ||
            row.status === 'OFFERED_TEST' ||
            row.status === 'IN_PROGRESS'
          ) {
            return `${value.toLocaleString('en-US')} (pending)`;
          }

          if (row.status === 'SUCCEEDED') {
            return `${value.toLocaleString('en-US')} (granted on ${moment(
              row.bonusTimestamp,
            ).format('MMM DD, YYYY')})`;
          }

          return null;
        },
      },
      {
        key: 'readCounter',
        header: 'Read/Watch',
        width: 100,
        renderValue: (value, row) => {
          if (row.category === 'VIDEO') {
            const watchList = Object.entries(row.data.watchList || {});

            return (
              <div>
                {watchList
                  .sort(([, a], [, b]) => new Date(b.timestamp) - new Date(a.timestamp))
                  .slice(0, 5)
                  .map(([key, v]) => {
                    const minutes = Math.floor(v.duration / 60);
                    const seconds = v.duration - minutes * 60;

                    return (
                      <div key={key}>
                        {`${minutes > 9 ? '' : '0'}${minutes}:${seconds > 9 ? '' : '0'}${seconds}`}
                      </div>
                    );
                  })}
                {watchList.length > 5 && <div>{`${watchList.length - 5} + more`}</div>}
              </div>
            );
          }

          return value?.toLocaleString('en-US');
        },
      },
      {
        key: 'data',
        header: 'Days left',
        width: 100,
        renderValue: (value, row) => {
          if (row.status === 'IN_PROGRESS') {
            if (row.category === 'CHALLENGE') {
              return value.daysToComplete;
            }

            return `${value.daysToComplete - moment.utc().diff(row.subscriptionTimestamp, 'days')}`;
          }

          return null;
        },
      },
      {
        key: 'results',
        header: 'Results',
        width: 200,
        renderValue: value => <div className="bo-text-pre-line">{value}</div>,
      },
      {
        key: 'offerTimestamp',
        header: 'Offered at',
        width: 120,
        renderValue: value => (value ? moment(value).format('MMM DD, YYYY') : null),
      },
      {
        key: 'subscriptionTimestamp',
        header: 'Subscribed at',
        width: 120,
        renderValue: (value, row) => {
          if (row.status !== 'OFFERED' && row.status !== 'OFFERED_TEST') {
            return moment(value).format('MMM DD, YYYY');
          }

          return null;
        },
      },
      {
        key: 'closeTimestamp',
        header: 'Closed at',
        width: 120,
        renderValue: value => (value ? moment(value).format('MMM DD, YYYY') : null),
      },
      {
        key: 'id',
        header: '',
        width: 100,
        renderValue: value => (
          <Button
            className="bo-common-inline-button"
            onClick={() => rewardRequest('delete', value)}
          >
            Delete
          </Button>
        ),
      },
    ],
    [rewardRequest],
  );

  const filteredRewards = useMemo(
    () =>
      rewards
        .map(x => ({ ...x, readCounter: x?.data?.readCounter ?? null }))
        .filter(trial => (selectedCategory ? trial.category === selectedCategory : true)),
    [rewards, selectedCategory],
  );

  const getRowHeight = useCallback(
    index => {
      const resultsRows = filteredRewards[index].results?.split('\n')?.length || 1;
      const resultsWidthInOneLine = getTextWidthInOneLine(filteredRewards[index].results);

      const watchListValues = Object.values(filteredRewards[index].data?.watchList || {});
      const watchListRows = watchListValues.length > 5 ? 6 : watchListValues.length;

      return Math.max(
        resultsRows * 18 + 18,
        resultsWidthInOneLine > 180 ? Math.ceil((resultsWidthInOneLine / 200) * 20) + 36 : 0,
        watchListRows * 18 + 18,
        36,
      );
    },
    [filteredRewards],
  );

  return (
    <div className="h-100 w-100">
      <div className="m-3 d-flex gap-3 justify-content-between overflow-hidden">
        <div className="d-flex gap-3">
          <Button
            className="bo-common-button text-nowrap"
            disabled={spinner}
            onClick={() => rewardRequest('processAll')}
          >
            Process
          </Button>
          <Form.Select
            className="bo-consumer-popover-form-menu-input bo-w-250"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            disabled={spinner}
          >
            <option value="">All Categories</option>
            {[...new Set(rewards.map(trial => trial.category))].map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </Form.Select>
        </div>
        <Button
          className="bo-common-button text-nowrap"
          disabled={spinner}
          onClick={() => rewardRequest('deleteAll')}
        >
          Delete All
        </Button>
      </div>
      <AutoSizer>
        {({ height, width }) => (
          <Spinner visible={spinner} overlayWidth={width}>
            <VirtualizedList
              tableRows={filteredRewards}
              tableColumns={tableColumns}
              width={width}
              height={height - 62}
              rowKey="id"
              getRowHeight={getRowHeight}
              headerTopBorder
            />
          </Spinner>
        )}
      </AutoSizer>
    </div>
  );
}

export default memo(ConsumerRewardsTab);
